import { useEffect, useRef, useState } from "react";
import { useNotifications, NOTIFICATION_TYPE_ICONS } from "@/hooks/useNotifications";

export default function NotificationBell() {
  const { data, unreadCount, markRead, markAllRead, reload } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    // Panel açıldığında bildirimleri tazele.
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const list = data ?? [];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Bildirimler${unreadCount > 0 ? ` (${unreadCount} okunmamış)` : ""}`}
        className="relative w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors"
      >
        <i className="ri-notification-3-line text-xl" aria-hidden="true"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Bildirimler"
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-lg border border-background-200 bg-background-50 z-50 animate-fade-down overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
            <span className="text-sm font-semibold text-foreground-950">Bildirimler</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                Tümünü okundu işaretle
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {list.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <i className="ri-notification-off-line text-3xl text-foreground-300" aria-hidden="true"></i>
                <p className="mt-2 text-sm text-foreground-500">Henüz bildirim yok</p>
              </div>
            ) : (
              list.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => markRead(n.id)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-background-100 transition-colors border-b border-background-100 ${
                    n.read ? "" : "bg-primary-50/40"
                  }`}
                >
                  <span className="w-9 h-9 shrink-0 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center">
                    <i className={NOTIFICATION_TYPE_ICONS[n.type] ?? "ri-notification-3-line"} aria-hidden="true"></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground-900 truncate">{n.title}</span>
                      <span className="text-[11px] text-foreground-400 shrink-0 whitespace-nowrap">{n.createdAt}</span>
                    </span>
                    {n.body && <span className="block mt-0.5 text-xs text-foreground-600 leading-relaxed">{n.body}</span>}
                  </span>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 shrink-0" aria-hidden="true"></span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}